import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  useTheme,
  Skeleton
} from '@mui/material';
import '../../Css/Layout/LayoutConstants.css';

type InfoCardProps = {
  title: string;
  value?: number | React.ReactNode;
  value2?: number | React.ReactNode;
  title1?: string;
  title2?: string;
  title1Color?: string;
  title2Color?: string;
  icon?: React.ReactNode;
  iconColor?: 'error' | 'primary' | 'secondary' | 'warning' | 'info' | 'success';
  formatAsCurrency?: boolean;
  size?: number;
  hideTitle?: boolean;
  loading?: boolean;
};

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  value,
  value2,
  title1,
  title2,
  title1Color = 'text.secondary',
  title2Color = 'text.secondary',
  icon,
  iconColor = 'primary',
  formatAsCurrency,
  size = 3,
  hideTitle = false,
  loading = false
}) => {
  const theme = useTheme();

  const renderValue = (val: number | React.ReactNode) => {
    if (React.isValidElement(val)) return val;
    if (typeof val === 'string') return val;
    if (typeof val === 'number') {
      return formatAsCurrency
        ? val.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
          })
        : val.toLocaleString('pt-BR');
    }
    return '-';
  };

  const noValues = value === undefined && value2 === undefined;

  // Ajuste para dark mode
  const iconBgColor = theme.palette.mode === 'dark'
    ? theme.palette[iconColor].dark
    : theme.palette[iconColor].light;

  const iconColorValue = theme.palette[iconColor].main;

  // Altura dinâmica para cards sem valores
  const cardHeightClass = noValues ? '' : 'card-size-sm';

  // Render skeleton quando está carregando
  if (loading) {
    return (
      <Grid item xs={12} md={size} lg={size} sx={{ pb: 1 }}>
        <Card
          elevation={1}
          sx={{

            bgcolor: theme.palette.background.paper,

            height: '100%',
            width: '100%',
            display: 'flex',
            '&:hover': {

            }
          }}
          className={cardHeightClass}
        >
          <CardContent sx={{
            height: '100%',
            width: '100%',
            padding: noValues ? '8px 8px 8px' : '8px 8px 8px',
            '&:last-child': { paddingBottom: noValues ? '8px' : '8px' }
          }}>
            {!hideTitle && (
              <Grid container alignItems="center" wrap="nowrap" sx={{ mb: 0 }}>
                {icon && (
                  <Grid item sx={{
                    mr: 0.75,
                    bgcolor: iconBgColor,
                    color: iconColorValue,
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Grid>
                )}
                <Grid item xs>
                  <Skeleton variant="text" width="70%" height={20} />
                </Grid>
              </Grid>
            )}

            {noValues ? (
              <Box sx={{ height: '8px' }} />
            ) : (
              value2 ? (
                <Grid container alignItems="center" sx={{ mt: '-6px', height: '100%' }}>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {title1 && (
                      <Skeleton variant="text" width="60%" height={14} sx={{ mb: 0.5 }} />
                    )}
                    <Skeleton variant="text" width="80%" height={24} />
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {title2 && (
                      <Skeleton variant="text" width="60%" height={14} sx={{ mb: 0.5 }} />
                    )}
                    <Skeleton variant="text" width="80%" height={24} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container alignItems="center" justifyContent="center" sx={{ mt: '-6px', height: '100%' }}>
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Skeleton variant="text" width="60%" height={32} />
                  </Grid>
                </Grid>
              )
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} md={size} lg={size} sx={{ pb: 1 }}>
      <Card
        elevation={1}
        sx={{
          borderRadius: 'var(--border-radius-md)',
          bgcolor: theme.palette.background.paper,
          boxShadow: 'var(--shadow-card)',
          height: '100%',
          width: '100%',
          display: 'flex',
          '&:hover': {
            boxShadow: 'var(--shadow-card-hover)',
          }
        }}
        className={cardHeightClass}
      >
        <CardContent sx={{ 
          height: '100%', 
          width: '100%', 
          padding: noValues ? '8px 8px 8px' : '8px 8px 8px', 
          '&:last-child': { paddingBottom: noValues ? '8px' : '8px' } 
        }}>
          {!hideTitle && (
            <Grid container alignItems="center" wrap="nowrap" sx={{ mb: 0 }}>
              {icon && (
                <Grid item sx={{
                  mr: 0.75,
                  bgcolor: iconBgColor,
                  color: iconColorValue,
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {icon}
                </Grid>
              )}
              <Grid item xs>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.2,
                    fontWeight: 400,
                    fontSize: '0.95rem'
                  }}
                >
                  {title}
                </Typography>
              </Grid>
            </Grid>
          )}

          {noValues ? (
            <Box sx={{ height: '8px' }} />
          ) : (
            value2 ? (
              <Grid container alignItems="center" sx={{ mt: '-6px', height: '100%' }}>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {title1 && (
                      <Typography variant="caption" sx={{ color: title1Color, mb: 0.5, fontWeight: 500, lineHeight: 1, width: '100%' }}>
                        {title1}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, lineHeight: 1.1, fontWeight: 600 }}>
                      {renderValue(value)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {title2 && (
                      <Typography variant="caption" sx={{ fontWeight: 500, color: title2Color, mb: 0.5, lineHeight: 1, width: '100%', textAlign: 'right' }}>
                        {title2}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, lineHeight: 1.1, fontWeight: 600 }}>
                      {renderValue(value2)}
                    </Typography>
                  </Grid>
                </Grid>
            ) : (
              <Grid container alignItems="center" justifyContent="center" sx={{ mt: '-6px', height: '100%' }}>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography 
                    variant="h5" 
                    fontWeight={600} 
                    textAlign="center" 
                    sx={{ color: theme.palette.text.primary, lineHeight: 1.1 }}
                  >
                    {renderValue(value)}
                  </Typography>
                </Grid>
              </Grid>
            )
          )
          }
        </CardContent>
      </Card>
    </Grid>
  );
};

export default InfoCard;
